import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import Dropdown from '@restart/ui/Dropdown'
import clsx from 'clsx'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ColumnAdapter,
  FunctionColumnValue,
  KEY_ARROW_LEFT,
  KEY_ARROW_RIGHT,
  KEY_BACKSPACE,
  KEY_DELETE,
  TableAdapter,
} from '@makyoapp/makyo-common'
import { Plus16, Search16, Textfield } from '@makyoapp/makyo-icon/icon'
import { Chip, DD } from '@makyoapp/makyo-ui'

import { NUM_REGEX } from '@/shared/constants'

import { RenderColumn } from '../RenderColumn'
import { useFilterColumnTypes } from './ColumnOrCustomDd'

interface FieldColumnDdMultiProps {
  /**
   * html id
   */
  id: string
  /**
   * allowed column type
   */
  types?: ColumnTypes[]
  /**
   * excluded column type
   * applied after allowed types
   */
  excludeTypes?: ColumnTypes[]
  options?: ColumnAdapter[]
  table: TableAdapter
  value: FunctionColumnValue[]
  onChange: (value: FunctionColumnValue[]) => void
  customValueType?: 'text' | 'number' | 'date'
  divClassName?: string
  withCustomValue?: boolean
  excludeColumns?: string[]
  withExtraOptions?: 'date'[]
}

function ValueChip({
  value: v,
  colOptions: columnOptions,
  customValueType,
  onChange,
  rawValue,
  index,
}: {
  colOptions: ColumnAdapter[]
  customValueType: 'text' | 'number' | 'date'
  value: FunctionColumnValue
  onChange: (value: FunctionColumnValue[]) => void
  rawValue: FunctionColumnValue[]
  index: number
}) {
  const { t } = useTranslation()

  const theCol = v.isCustom ? undefined : columnOptions.find((c) => c.id === v.value)
  return (
    <Chip
      onRemove={() => {
        const copy = [...rawValue]
        copy.splice(index, 1)
        onChange(copy)
      }}
    >
      <div>
        {v.isCustom ? (
          <input
            onPaste={(e) => {
              // const data = e.clipboardData.getData('Text')
              // if (!NUM_REGEX.test(data)) {
              //   e.preventDefault()
              // }
            }}
            onKeyDown={(e) => {
              if (customValueType === 'number') {
                if (
                  !NUM_REGEX.test(e.key) &&
                  !e.ctrlKey &&
                  ![KEY_BACKSPACE, KEY_DELETE, KEY_ARROW_LEFT, KEY_ARROW_RIGHT].includes(e.key)
                ) {
                  e.preventDefault()
                }
              }
            }}
            placeholder={t('Enter Custom')}
            className='min-w-0 bg-transparent border bdr'
            type={customValueType}
            value={v.value?.toString()}
            onChange={(e) => {
              const val = e.currentTarget.value
              const copy = [...rawValue]
              copy[index].value = val
              onChange(copy)
            }}
          />
        ) : (
          <RenderColumn col={theCol} />
        )}
      </div>
    </Chip>
  )
}
export function ColumnOrCustomDdMulti({
  id,
  table,
  types = [],
  excludeTypes = [],
  value: rawValue,
  onChange,
  customValueType = 'text',
  options: colOpts,
  divClassName,
  withCustomValue,
  excludeColumns,
  withExtraOptions,
}: FieldColumnDdMultiProps) {
  const columnOptions = useFilterColumnTypes(colOpts, table, types, excludeTypes, excludeColumns, customValueType)

  /*const options = useMemo(() => {
    const opts = []
    if (withCustomValue) {
      opts.push(enterCustomOption)
      if (withExtraOptions?.includes('date')) {
        opts.push(
          `#{${hrPlaceholder || t('Date Preset')}}#` as never,
          ...extraDateOptions
        )
      }
      if (!customTypeOnly) {
        opts.push(
          `#{${hrPlaceholder || t('Columns')}}#` as never,
          ...(columnOptions || [])
        )
      }
    } else {
      opts.push(selectOption)
      if (withExtraOptions?.includes('date')) {
        opts.push(
          `#{${hrPlaceholder || t('Date Preset')}}#` as never,
          ...extraDateOptions
        )
      }
      if (!customTypeOnly) {
        opts.push(
          `#{${hrPlaceholder || t('Columns')}}#` as never,
          ...(columnOptions || [])
        )
      }
    }

    return opts
  }, [columnOptions])*/
  /*
  const rawValueId = useMemo(() => {
    return rawValue.map((rv) => rv.value)
  }, [rawValue])

  const value = useMemo(() => {
    return rawValueId
      ? columnOptions.filter((o) => rawValueId.includes(o.id)) || [
          columnOptions[0]
        ]
      : [columnOptions[0]]
  }, [columnOptions, rawValueId])*/
  const [search, setSearch] = useState('')
  const cols = useMemo(() => {
    if (search) {
      return columnOptions.filter((c) => `${c.name} ${c.type}`.toLowerCase().includes(search.toLowerCase()))
    } else {
      return columnOptions
    }
  }, [search, columnOptions])

  return (
    <div className={clsx('w-full min-w-0', divClassName)}>
      <div className={clsx('w-full min-w-0 flex gap-2 flex-wrap', rawValue.length > 0 && 'mb-2')}>
        {rawValue.map((v, i) => {
          return (
            <ValueChip
              key={i}
              value={v}
              colOptions={columnOptions}
              customValueType={customValueType}
              onChange={onChange}
              rawValue={rawValue}
              index={i}
            />
          )
        })}
      </div>
      <div>
        <DD
          toggle={() => {
            return (
              <div className='flex-cc h-9 w-9 bg-primary-500 text-white rounded-full'>
                <Plus16 />
              </div>
            )
          }}
          id={id}
          menuWrapperClassName='sdd-menu-subc text-m-m'
          menuClassName='min-w-[260px] z-100'
          menuNoPad
          portalMenu
        >
          <div className='sdd-search-wrap'>
            <Search16 className='sdd-search-icon' />
            <input
              type='text'
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value)
              }}
              className='sdd-search-input dark:text-white'
            />
          </div>
          <div className='max-h-[220px] overflow-y-auto'>
            {withCustomValue !== false && (
              <>
                <Dropdown.Item
                  onClick={() => {
                    const copy = [...rawValue]
                    copy.push({
                      isCustom: true,
                      value: '',
                    })
                    onChange(copy)
                  }}
                  className='flex-c sdd-item mk-ag-sdd'
                >
                  <Textfield className='fill-primary-500' />
                  <Trans>Enter Custom</Trans>
                </Dropdown.Item>
                <div className='sdd-heading px-4! py-2 text-light border-t bdr'>
                  <Trans>Columns</Trans>
                </div>
              </>
            )}
            {cols.map((opt) => {
              return (
                <Dropdown.Item
                  onClick={() => {
                    const copy = [...rawValue]
                    copy.push({
                      isCustom: false,
                      value: opt.id,
                    })
                    onChange(copy)
                  }}
                  className='sdd-item mk-ag-sdd'
                  key={opt.id}
                >
                  <RenderColumn col={opt} />
                </Dropdown.Item>
              )
            })}
          </div>
        </DD>
      </div>
    </div>
  )
}
